import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet/dist/leaflet.css"

// Fix Leaflet's default icon paths when served via Vite.
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png"
})

// Map + address geocoding picker.
//
// Markup expectations:
//   [data-controller="map-picker"] wraps the whole thing
//   [data-map-picker-target="address"] text input
//   [data-map-picker-target="lat"]     hidden number input
//   [data-map-picker-target="lng"]     hidden number input
//   [data-map-picker-target="map"]     empty div where the map renders
//   [data-map-picker-target="status"]  (optional) span for geocode feedback
//
// Values:
//   defaultLatValue  default map center latitude (city center)
//   defaultLngValue  default map center longitude
//   defaultZoomValue default map zoom (e.g. 13)
//   userAgentValue   identifier for Nominatim TOS
export default class extends Controller {
  static targets = ["address", "lat", "lng", "map", "status"]
  static values = {
    defaultLat: Number,
    defaultLng: Number,
    defaultZoom: { type: Number, default: 13 },
    contactEmail: { type: String, default: "" }
  }

  connect() {
    const initialLat = parseFloat(this.latTarget.value)
    const initialLng = parseFloat(this.lngTarget.value)
    const hasPin = Number.isFinite(initialLat) && Number.isFinite(initialLng)

    const center = hasPin
      ? [initialLat, initialLng]
      : [this.defaultLatValue || 0, this.defaultLngValue || 0]

    this.map = L.map(this.mapTarget).setView(center, this.defaultZoomValue)

    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19
    }).addTo(this.map)

    if (hasPin) {
      this.placeMarker(initialLat, initialLng)
    }

    // Click anywhere on the map to place/move the pin.
    this.map.on("click", (e) => {
      this.placeMarker(e.latlng.lat, e.latlng.lng)
    })
  }

  disconnect() {
    this.map?.remove()
  }

  // Triggered by the "Find on map" button next to the address input.
  async lookup(event) {
    event?.preventDefault()
    const query = this.addressTarget.value.trim()
    if (!query) {
      this.setStatus("Type an address first.")
      return
    }

    this.setStatus("Searching…")

    try {
      const url = new URL("https://nominatim.openstreetmap.org/search")
      url.searchParams.set("q", query)
      url.searchParams.set("format", "json")
      url.searchParams.set("limit", "1")
      if (this.contactEmailValue) url.searchParams.set("email", this.contactEmailValue)

      const response = await fetch(url.toString(), { headers: { "Accept": "application/json" } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const results = await response.json()
      if (!results.length) {
        this.setStatus("No match. Drop the pin manually.")
        return
      }

      const { lat, lon } = results[0]
      const latNum = parseFloat(lat)
      const lngNum = parseFloat(lon)
      this.placeMarker(latNum, lngNum)
      this.map.setView([latNum, lngNum], 16)
      this.setStatus("Pin placed — drag to fine-tune.")
    } catch (err) {
      console.error("Nominatim lookup failed", err)
      this.setStatus("Lookup failed. Drop the pin manually.")
    }
  }

  placeMarker(lat, lng) {
    if (this.marker) {
      this.marker.setLatLng([lat, lng])
    } else {
      this.marker = L.marker([lat, lng], { draggable: true }).addTo(this.map)
      this.marker.on("dragend", () => {
        const pos = this.marker.getLatLng()
        this.writeCoords(pos.lat, pos.lng)
        this.setStatus("Pin moved.")
      })
    }
    this.writeCoords(lat, lng)
  }

  writeCoords(lat, lng) {
    this.latTarget.value = lat.toFixed(6)
    this.lngTarget.value = lng.toFixed(6)
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }
}
